import isNumber from 'lodash/isNumber';
import {ChartableStep, CountType, Step} from './types';

export function getYInterval(maxStepCount) {
  if (maxStepCount === 0) {
    return 0;
  } else if (maxStepCount < 5) {
    return maxStepCount + (maxStepCount % 2);
  }

  // Create nice intervals that take advantage of the fact that there are 6
  // visible subdivisions on the y axis.
  const interval = maxStepCount / 7;

  // Determine a nice interval thats some power of 10 multiple of 1, 2, or 5
  const exp = Math.floor(Math.log(interval) / Math.log(10));
  const f = interval / Math.pow(10, exp);
  let niceInterval = Math.pow(10, exp + 1);
  if (f < 1.1) {
    niceInterval = Math.pow(10, exp);
  } else if (f <= 2) {
    niceInterval = 2 * Math.pow(10, exp);
  } else if (f <= 3) {
    niceInterval = 3 * Math.pow(10, exp);
  } else if (f <= 5) {
    niceInterval = 5 * Math.pow(10, exp);
  }

  // Let the first label be the next niceInterval greater than maxStepCount
  return niceInterval;
}

export const minimumBarHeight = 3;

function clampHeight(height) {
  return Math.max(minimumBarHeight, height || 0);
}

/**
 * @return percentage height of the bar between 0 - 100
 */
export function barHeight(
  value: number,
  stepIndex: number,
  barIndex: number,
  overallSteps: Step[],
  countType: CountType,
  maxOverallStepValue: number,
): number {
  if (countType === CountType.RELATIVE && isNumber(stepIndex) && isNumber(barIndex)) {
    // If ndx = 0 and value = 0, all bars should be minimum height
    if (stepIndex === 0) {
      return value === 0 ? minimumBarHeight : 100;
    }
    const maxSegmentStepValue = overallSteps[0].data[barIndex].value;
    return clampHeight((value / maxSegmentStepValue) * 100);
  } else {
    return clampHeight((value / maxOverallStepValue) * 100);
  }
}

export function dropOffBarHeight(
  step: Step,
  idx: number,
  barIdx: number,
  overallSteps: Step[],
  countType: CountType,
  maxOverallStepValue: number,
): number {
  if (idx === 0) {
    return 0;
  }
  const currStepBarHeight =
    barHeight(step.data[barIdx].value, idx, barIdx, overallSteps, countType, maxOverallStepValue) / 100;
  const prevStepBarHeight =
    barHeight(overallSteps[idx - 1].data[barIdx].value, idx, barIdx, overallSteps, countType, maxOverallStepValue) /
    100;
  return clampHeight((prevStepBarHeight - currStepBarHeight) * 100);
}

/** precalculate the height of each bar */
export function chartableSteps(steps: Step[], countType: CountType, maxOverallStepValue: number): ChartableStep[] {
  return steps.map((step, stepIdx) => {
    return {
      ...step,
      data: step.data.map((bar, barIdx) => {
        // Calculate bar heights
        return {
          ...bar,
          dropOffBarHeight: dropOffBarHeight(step, stepIdx, barIdx, steps, countType, maxOverallStepValue),
          convertedBarHeight: barHeight(bar.value, stepIdx, barIdx, steps, countType, maxOverallStepValue),
        };
      }),
    };
  });
}
